import React, { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { useRouter } from 'expo-router';
import { useCollectionStore, useModalStore, useToastStore, useChatInputStore, useModelStore, useCoworkerStore } from '@/store';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import DefaultRouteNavigationHeaderLeft from '@/components/Navigation/Routes/DefaultRouteNavigationHeaderLeft';
import CollectionDropdown from '@/components/Dropdowns/CollectionDropdown';
import { useModalHelper } from '@/hooks/useModalHelper';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { getDescendantIds, MAX_FOLDER_DEPTH, getItemDepth } from '@/constants/utils/folder_tree';

const EMPTY_ARRAY = [];

const createStyles = (colors) => StyleSheet.create({
  modelSelectorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    backgroundColor: colors.solid_color_block,
    minWidth: 80,
    maxWidth: 200,
  },
  modelSelectorContainerHovered: {
    backgroundColor: colors.solid_color_tertiary,
  },
  modelImage: {
    width: 20,
    height: 20,
    borderRadius: 0,
    backgroundColor: '#fff',
  },
  modelName: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text_color_primary,
    maxWidth: 100,
  },
  chevron: {
    marginLeft: 2,
  },
});

// Model selector for collection new-chat composer (matches home/chat mobile header)
const CollectionHeaderModelSelector = React.memo(({ collectionId }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { openModal } = useModalStore(state => state.actions);
  const setContext = useChatInputStore(state => state.setContext);
  const setSelectedModels = useChatInputStore(state => state.setSelectedModels);

  // Collection composer uses `new-${collectionId}` as ChatInput context
  const contextId = collectionId ? `new-${collectionId}` : 'new';
  const selectedModelIds = useChatInputStore(state =>
    state.inputStates?.[contextId]?.selectedModelIds ?? EMPTY_ARRAY
  );

  const models = useModelStore(state => state.models);

  const selectedModels = useMemo(() => {
    if (!selectedModelIds || selectedModelIds.length === 0) return [];
    return selectedModelIds
      .map(id => {
        const modelId = typeof id === 'string' ? id : id?.id;
        return models[modelId];
      })
      .filter(Boolean);
  }, [selectedModelIds, models]);

  const isAutoMode = selectedModels.length === 0;
  const displayName = isAutoMode
    ? t('nav.routes.home.auto')
    : selectedModels.length === 1
      ? selectedModels[0]?.name || t('nav.routes.chat.modelFallback')
      : `${selectedModels.length} ${t('nav.routes.home.modelsCount')}`;

  const handlePress = () => {
    openModal('ModelSelectorDetailedModal', {
      modelIds: selectedModelIds,
      onChange: (modelIds) => {
        setContext(contextId);
        setSelectedModels(modelIds);
      },
      maxModels: 5,
      noMultiSelectMode: false,
      showDoneButton: true,
      showSelectedModels: true,
    });
  };

  return (
    <Pressable
      style={({ hovered }) => [
        styles.modelSelectorContainer,
        hovered && styles.modelSelectorContainerHovered
      ]}
      onPress={handlePress}
    >
      {selectedModels.length > 0 && selectedModels[0]?.logoPath ? (
        <Image
          source={{ uri: selectedModels[0].logoPath }}
          style={styles.modelImage}
        />
      ) : null}
      <Text style={styles.modelName} numberOfLines={1}>
        {displayName}
      </Text>
      <Icon
        name="chevron-down"
        size={12}
        color={colors.text_color_tertiary}
        style={styles.chevron}
      />
    </Pressable>
  );
});

CollectionHeaderModelSelector.displayName = 'CollectionHeaderModelSelector';

const CollectionRouteNavigationHeaderRight = React.memo(({ collection }) => {
  const { t } = useTranslation();
  const router = useRouter();
  
  const { useInputModal, useConfirmationModal } = useModalHelper();
  const showInputModal = useInputModal();
  const showConfirmationModal = useConfirmationModal();
  
  const renameCollection = useCollectionStore(state => state.renameCollection);
  const updateCollection = useCollectionStore(state => state.updateCollection);
  const deleteCollection = useCollectionStore(state => state.deleteCollection);
  const collectionsMap = useCollectionStore(state => state.collections);
  const collectionIcon = collectionsMap[collection?.id]?.icon || 'folder';
  const { openModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);

  const handleRename = useCallback(() => {
    showInputModal({ 
      title: t('nav.routes.collection.renameTitle'),
      subtitle: t('nav.routes.collection.renameSubtitle'),
      placeholder: t('nav.routes.collection.collectionName'),
      primaryButtonTitle: t('common.save'),
      initialValue: collection?.title,
      onSave: async (newName) => {
        try {
          await renameCollection(collection.id, newName);
        } catch (error) {
          console.error('Failed to rename collection:', error);
          addToast(t('nav.routes.collection.failedToRename'), 'error');
        }
      },
    });
  }, [collection?.title, collection?.id, renameCollection, showInputModal, addToast, t]);

  const handleDelete = useCallback(() => {
    showConfirmationModal({
      title: `${t('common.delete')} ${collection?.title || t('nav.routes.collection.collectionFallback')}`,
      message: t('nav.routes.collection.deleteMessage'),
      confirmText: t('common.delete'),
      isDangerous: true,
      onConfirm: async () => {
        try {
          await deleteCollection(collection.id);
          router.push('/files');
        } catch (error) {
          console.error('Failed to delete collection:', error);
          addToast(t('nav.routes.collection.failedToDelete'), 'error');
        }
      },
    });
  }, [collection?.title, collection?.id, deleteCollection, router, showConfirmationModal, addToast, t]);

  const handleChangeIcon = useCallback(() => {
    openModal('CollectionsIconGridModal', {
      selectedIcon: collectionIcon,
      onIconSelect: async (iconKey) => {
        try {
          await updateCollection(collection.id, { icon: iconKey });
          addToast(t('nav.routes.collection.iconUpdated'), 'success');
        } catch (error) {
          console.error('Failed to update collection icon:', error);
          addToast(t('nav.routes.collection.failedToUpdateIcon'), 'error');
        }
      },
    });
  }, [openModal, collectionIcon, collection?.id, updateCollection, addToast, t]);

  const handleSettings = useCallback(() => {
    openModal('CollectionSettingsModal', {
      onSave: async ({ customInstructions, memoryEnabled, defaults }) => {
        try {
          await updateCollection(collection.id, {
            customInstructions,
            memoryEnabled,
            defaults,
          });
          addToast(t('collectionSettings.saved'), 'success');
        } catch (error) {
          console.error('Failed to update collection settings:', error);
          addToast(t('collectionSettings.saveFailed'), 'error');
        }
      },
      collectionTitle: collection?.title || 'Collection',
      initialDefaults: collection?.defaults || {},
      initialInstructions: collection?.customInstructions || '',
      initialMemoryEnabled: collection?.memoryEnabled !== false,
    });
  }, [openModal, collection, updateCollection, addToast, t]);

  const handleAddFiles = useCallback(() => {
    openModal('AddFilesToCollectionModal', {
      collectionId: collection.id,
      collectionTitle: collection?.title || 'Collection',
    });
  }, [openModal, collection?.id, collection?.title]);

  const handleCreateTask = useCallback(() => {
    openModal('CreateTaskModal', {
      collectionId: collection.id,
      collectionTitle: collection?.title || 'Collection',
    });
  }, [openModal, collection?.id, collection?.title]);

  const { isXS } = useResponsiveness();

  const handleNewSubcollection = useCallback(() => {
    const collections = Object.values(collectionsMap || {});
    if (getItemDepth(collections, collection?.id) >= MAX_FOLDER_DEPTH - 1) {
      addToast(t('pages.collections.nestedTooDeep'), 'error');
      return;
    }
    openModal('CreateCollectionModal', {
      parentId: collection?.id,
      parentTitle: collection?.title,
    });
  }, [collectionsMap, collection?.id, collection?.title, addToast, t, openModal]);

  const handleMove = useCallback(() => {
    if (!collection?.id) return;
    const collections = Object.values(collectionsMap || {});
    const excludeIds = [collection.id, ...getDescendantIds(collections, collection.id)];
    openModal('MoveCollectionModal', {
      collectionId: collection.id,
      excludeIds,
    });
  }, [collectionsMap, collection?.id, openModal]);

  const TriggerComponent = React.memo(() => (
    <NavigationBarButton
      icon="ellipsis"
      title={t('nav.routes.collection.moreOptions')}
      tooltip={t('nav.routes.collection.moreOptions')}
    />
  ));

  // Desktop uses the in-page collection menu. Native / narrow screens keep
  // this header overflow so settings stay one tap away.
  if (!isXS) {
    return null;
  }

  if (!collection?.title) {
    return (
      <NavigationBarButtonContainer rightPadding>
        <NavigationBarButton
          icon="home"
          title="Home"
          onPress={() => router.push('/app')}
        />
      </NavigationBarButtonContainer>
    );
  }

  return (
    <NavigationBarButtonContainer rightPadding>
      <CollectionDropdown
        TriggerComponent={TriggerComponent}
        handleSettings={handleSettings}
        handleAddFiles={handleAddFiles}
        handleCreateTask={handleCreateTask}
        handleChangeIcon={handleChangeIcon}
        handleRename={handleRename}
        handleDelete={handleDelete}
        handleNewSubcollection={handleNewSubcollection}
        handleMove={handleMove}
        title={collection?.title}
      />
    </NavigationBarButtonContainer>
  );
});

CollectionRouteNavigationHeaderRight.displayName = 'CollectionRouteNavigationHeaderRight';

function CollectionRouteNavigationHeaderLeft() {
  return <DefaultRouteNavigationHeaderLeft />;
}

const CollectionHeaderTitle = React.memo(({ collectionId }) => {
  const { isXS } = useResponsiveness();
  const isWorkMode = useCoworkerStore((s) => s.composerMode === 'coworker');

  // Chat composer on mobile uses the header model picker. Work ignores
  // collection chat defaults, so don't show it in Work mode.
  if (isXS && !isWorkMode) {
    return <CollectionHeaderModelSelector collectionId={collectionId} />;
  }

  return null;
});

CollectionHeaderTitle.displayName = 'CollectionHeaderTitle';

function getCollectionRouteNavigationOptions(collection, collectionId) {
  const id = collection?.id || collectionId;
  const title = collection?.title || 'Collection';

  return {
    title,
    headerTitle: () => <CollectionHeaderTitle collectionId={id} />,
    headerBackVisible: false,
    headerLeft: () => <CollectionRouteNavigationHeaderLeft />,
    headerRight: () => <CollectionRouteNavigationHeaderRight collection={collection} />
  };
}

export default getCollectionRouteNavigationOptions;
